import { type InvestmentsReportTemplate, InvestmentsReportTemplateControllerApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { storyfolioV2Repository } from "$root/functional-areas/storyfolio-v2/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import type { Abortable } from "@mdotm/utils/promise";
import { defaultPortfolioTemplates } from "./default-templates";
import { type ExportTemplate, toStoryfolioV2Template } from "./export-templates";

const lists = {
	templates: "templates",
	exportTemplates: "exportTemplates",
};

const { newQuery, newAction, invalidateEntity, invalidateLists } = repositoryHelperFactory("reports", {
	listKeys: Object.values(lists),
});

const investmentsReportTemplateApi = () => getApiGen(InvestmentsReportTemplateControllerApiFactory);

/** The built-in defaults, plus the customer's own templates that do not shadow one by name. */
async function fetchReportBuilderTemplates(): Promise<Array<InvestmentsReportTemplate & { id?: string }>> {
	const defaults = [
		...defaultPortfolioTemplates.map((defaultTemplate) => ({
			...defaultTemplate,
			visible: true,
		})),
	];
	const list = await axiosExtract(investmentsReportTemplateApi().listInvestmentReportTemplates());
	return [...defaults, ...list.filter((x) => !defaults.some((y) => x.templateName === y.templateName))];
}

export const reportRepository = {
	invalidateEntity,
	invalidateLists,

	/**
	 * The Report Builder's own templates: the ones this repository creates, renames and deletes.
	 *
	 * The export menus read {@link exportTemplates} instead. Keeping the two apart is what holds
	 * Storyfolio V2's entries out of the Report Builder's management surfaces, where they
	 * are not editable.
	 */
	templates: newQuery(lists.templates, fetchReportBuilderTemplates),

	/**
	 * Every template the export menus offer, from both engines, in one list.
	 *
	 * Storyfolio V2's entries are appended rather than sectioned: a user picks a template,
	 * and which engine renders it is not theirs to know. A gateway that is absent, unentitled or
	 * failing contributes nothing — the Report Builder's own list is the whole answer then, because
	 * an export menu that disappears when a second service is down is a regression on the first.
	 */
	exportTemplates: newQuery(lists.exportTemplates, async (): Promise<ExportTemplate[]> => {
		const [reportBuilderTemplates, storyfolioV2Templates] = await Promise.all([
			fetchReportBuilderTemplates(),
			storyfolioV2Repository.templates.fetchQuery().catch(() => []),
		]);
		return [...reportBuilderTemplates, ...storyfolioV2Templates.map(toStoryfolioV2Template)];
	}),

	template: newQuery("template", (uuid: string): Promise<InvestmentsReportTemplate & { id?: string }> => {
		const defaultTemplate = defaultPortfolioTemplates.find(({ id }) => id === uuid);
		if (defaultTemplate) {
			return Promise.resolve({ ...defaultTemplate, visible: true });
		}
		return axiosExtract(investmentsReportTemplateApi().getInvestmentReportTemplate(uuid));
	}),

	create: newAction(async (request: InvestmentsReportTemplate) => {
		const result = await axiosExtract(investmentsReportTemplateApi().createPTFReportTemplate(request));
		invalidateLists();
		return result;
	}),

	update: newAction(async (uuid: string, request: InvestmentsReportTemplate) => {
		const result = await axiosExtract(investmentsReportTemplateApi().updatePTFReportTemplate(uuid, request));
		invalidateEntity(uuid);
		return result;
	}),

	delete: newAction(async (uuid: string) => {
		await axiosExtract(investmentsReportTemplateApi().deletePTFReportTemplate(uuid));
		invalidateLists();
	}),

	isReportNameAvailable: newAction((name: string, opts?: Abortable) => {
		if (
			defaultPortfolioTemplates.some(
				({ templateName }) => templateName.trim().toLowerCase() === name.trim().toLowerCase(),
			)
		) {
			return Promise.resolve(false);
		}
		return axiosExtract(investmentsReportTemplateApi().checkTemplateNameAvailability(name, opts));
	}),
};
